import { ApiError, type components, unwrap } from '@shared/api-client/index';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api';

type User = components['schemas']['UserResponse'];

export const meKey = ['me'] as const;

/**
 * The signed-in user, or null when logged out (a 401 is the logged-out state,
 * not an error). `initialUser` seeds it from the SSR loader.
 */
export function useMe(initialUser: User | null) {
	return useQuery({
		queryKey: meKey,
		queryFn: async () => {
			try {
				return unwrap(await api.GET('/v1/me')).user;
			} catch (error) {
				if (error instanceof ApiError && error.status === 401) return null;
				throw error;
			}
		},
		initialData: initialUser,
		// Still revalidate on mount: in local dev the api cookie isn't on the app origin, so SSR seeds null.
		staleTime: 0,
	});
}

export function useSignIn() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (email: string) => api.POST('/v1/auth/dev-login', { body: { email } }).then(unwrap),
		onSuccess: () => queryClient.invalidateQueries(),
	});
}

export function useSignOut() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => api.POST('/v1/auth/logout').then(unwrap),
		// Drop every cached query in one call — scales as more queries are added.
		// Clearing /me flips `user` null, which disables the todos query before it
		// can refetch; a stray 401 from any in-flight query is the global onError's
		// job (a 401 -> redirect-to-auth interceptor), not per-query cleanup.
		onSuccess: () => queryClient.clear(),
	});
}
